import { SetStateAction, Dispatch, useEffect, useRef, useState } from "react"

import { PageData, PageInformation } from "types"
import { ViewerMetaData } from "src/models/viewer/ViewerMetaData"
import { ParagraphData } from "src/models/viewer/paragraph/ParagraphData"
import { EndImage } from "src/models/viewer/paragraph/meta/EndImage"
import { StartImage } from "src/models/viewer/paragraph/meta/StartImage"
import { ParagraphMetaData } from "src/models/viewer/paragraph/meta/ParagraphMetaData"

const imageCheck = (y: ParagraphMetaData) => {
  return y.type === "start-image" || y.type === "end-image"
}

type Image = {
  id: string
  start: number
  end: number
  src: string
}

// 挿絵のデータを整形
const imagePreProcess = (
  data: Array<string | ParagraphData>,
  meta: ViewerMetaData,
) => {
  const images = data
    .map((x, i) => ({ x, i }))
    .map(({ x, i }) => {
      if (typeof x === "string" || !x.meta) return null
      const image = x.meta.find(imageCheck)
      if (!image) return null

      return { x: image, i }
    })
    .reduce((prev, current) => {
      if (current) return [...prev, current]

      return prev
    }, Array<{ x: StartImage | EndImage; i: number }>())
    .reduce((prev, { x, i }) => {
      if (!prev[x.id]) prev[x.id] = []
      prev[x.id].push({ ...x, index: i })

      return prev
    }, {} as { [key: string]: Array<{ index: number } & (StartImage | EndImage)> })

  return Object.entries(images).map(([key, [start, end]]) => {
    const m = meta.images && meta.images.find((x) => x.id === start.id)

    return {
      id: key,
      start: start.index,
      end: end.index,
      src: m ? m.src : "",
    } as Image
  })
}

const getImage = (beginIndex: number, endIndex: number, images: Array<Image>) =>
  images.find(
    (x) =>
      // [x.start, x.end] の中に beginIndex または endIndex が含まれているかを確認
      (x.start <= beginIndex && beginIndex < x.end) ||
      (x.start < endIndex && endIndex < x.end),
  )

// pagesRef.current から image を挿絵にもつページの位置を取得
const getImageIndexFromPageDataList = (
  pageDataList: PageData[],
  image?: Image,
) => pageDataList.findIndex((d) => !!d.image && d.image === image?.src)

const needsInsertImage = (
  surplus: number,
  imageData: ParagraphData | string,
  pageData: Omit<PageData, "key" | "width">,
) =>
  surplus === 0 &&
  typeof imageData !== "string" &&
  imageData.meta &&
  imageData.meta.some((x) => x.type === "start-image") &&
  // pageData が最初のページの場合、最初のページが挿絵ページのため
  // 追加で挿絵ページの挿入が不要
  !(pageData.beginIndex === 0 && pageData.endIndex === 0)

const useViewerPreProcess = (
  data: Array<string | ParagraphData>,
  meta: ViewerMetaData,
  pageInformation: PageInformation,
  setIsInitialized: Dispatch<SetStateAction<boolean>>,
) => {
  const [maxPage, setMaxPage] = useState<number>(0)
  const [pageContent, setPageContent] = useState<HTMLDivElement | null>(null)
  const pagesRef = useRef<Array<PageData>>([])

  useEffect(() => {
    if (!pageContent || pageInformation.width === 0) {
      return
    }

    const lineWidths = Array.from(pageContent.children)
      // .overlay を除外
      .slice(1)
      .map((child) => child.getBoundingClientRect().width)

    pagesRef.current = []

    // image の範囲をデータ化
    const images = imagePreProcess(data, meta)

    let beginIndex = 0
    let endIndex = 0
    let surplus = 0

    while (endIndex < data.length) {
      const currentData = data[endIndex]
      const lastRowWidth = lineWidths[endIndex]
      const widthArr = lineWidths.slice(beginIndex, endIndex + 1)
      const w = widthArr.length > 0 ? widthArr.reduce((p, c) => p + c) : 0
      const rest = pageInformation.maxWidth - (w - surplus)

      let pageData = null
      if (
        typeof currentData !== "string" &&
        currentData.meta &&
        currentData.meta.some((x) => x.type === "start-image")
      ) {
        // 既に挿絵のページが存在するかどうかを確認
        // 「挿絵を挿入」の箇所で既に挿絵が挿入されている場合がある
        const image = getImage(endIndex, endIndex, images)
        const imageIndex = getImageIndexFromPageDataList(
          pagesRef.current,
          image,
        )

        // 挿絵のページが存在しない場合のみ挿絵のページを挿入
        if (imageIndex === -1) {
          // 挿絵の段落まで含みたくないので +1 しない
          pageData = {
            rightShift: surplus,
            data: data.slice(beginIndex, endIndex),
            beginIndex,
            endIndex,
            rest: rest,
          }
        }
        surplus = 0
        beginIndex = endIndex
        endIndex++
      } else if (data.length - 1 === endIndex) {
        pageData = {
          rightShift: surplus,
          data: data.slice(beginIndex, endIndex + 1),
          beginIndex,
          endIndex,
          rest: rest,
        }
        surplus = 0
      } else if (rest < 0) {
        // 段落がページをオーバーしているので、インクリメントしない
        pageData = {
          rightShift: surplus,
          data: data.slice(beginIndex, endIndex + 1),
          beginIndex,
          endIndex: endIndex + 1,
          rest: rest,
        }

        // 余りがあった場合は余剰を記録しておく
        surplus = lastRowWidth + rest
        beginIndex = endIndex
      } else if (rest < pageInformation.lineHeight) {
        pageData = {
          rightShift: surplus,
          data: data.slice(beginIndex, endIndex + 1),
          beginIndex,
          endIndex: endIndex + 1,
          rest: rest,
        }

        surplus = 0
        beginIndex = endIndex + 1
      } else {
        endIndex++
      }

      if (pageData) {
        const image = getImage(pageData.beginIndex, pageData.endIndex, images)

        // 挿絵の配列内の位置を取得
        const isShowImage = true

        pagesRef.current.push({
          ...pageData,
          image: isShowImage ? image?.src : undefined,
          key: `page${pagesRef.current.length}`,
          width: pageInformation.maxWidth,
        })

        // 挿絵を挿入
        const imageData = data[pageData.endIndex]

        if (needsInsertImage(surplus, imageData, pageData)) {
          const image = getImage(pageData.endIndex, pageData.endIndex, images)

          // 挿絵を表示すべきところで参照するソースがない場合、Not Found を表示する
          if (image?.src === "") {
            pagesRef.current.push({
              data: [],
              rightShift: 0,
              beginIndex: pageData.endIndex,
              endIndex: pageData.endIndex,
              rest: 0,
              image: "static_page/parts/img/plate_notfound.png",
              key: `page${pagesRef.current.length}`,
              width: pageInformation.maxWidth,
            })
          } else {
            pagesRef.current.push({
              data: [],
              rightShift: 0,
              beginIndex: pageData.endIndex,
              endIndex: pageData.endIndex,
              rest: 0,
              image: image?.src,
              key: `page${pagesRef.current.length}`,
              width: pageInformation.maxWidth,
            })
          }
        }

        // 終了
        if (data.length - 1 === endIndex) {
          // 挿絵の範囲が2段落の場合、最終段落まで表示する
          const type =
            typeof currentData !== "string" &&
            currentData.meta &&
            currentData.meta[0].type
          if (type !== "start-image") break
        }
      }
    }

    // 最後の段落がページをオーバーしている場合、最終ページを追加
    if (
      pagesRef.current[pagesRef.current.length - 1] &&
      pagesRef.current[pagesRef.current.length - 1].rest < 0
    ) {
      pagesRef.current.push({
        rightShift:
          lineWidths[endIndex] +
          pagesRef.current[pagesRef.current.length - 1].rest, // 最終段落の見切れていた部分のみを表示
        data: data.slice(endIndex, endIndex + 1),
        beginIndex: endIndex,
        endIndex,
        rest: 0,
        image: undefined,
        key: `page${pagesRef.current.length}`,
        width: pageInformation.maxWidth,
      })
    }

    setMaxPage(pagesRef.current.length)
    setIsInitialized(true)
  }, [data, meta, pageContent, pageInformation, setIsInitialized])

  const getPageData = (page: number): PageData => {
    const pageData = pagesRef.current[page]

    return pageData
      ? pageData
      : {
          data: [],
          rightShift: 0,
          rest: 0,
          beginIndex: 0,
          endIndex: 0,
          width: pageInformation.maxWidth,
          key: `empty_${page}`,
        }
  }

  return {
    calculateRef: setPageContent,
    getPageData,
    maxPage,
    pageData: pagesRef.current, // テスト用
  }
}
export default useViewerPreProcess
